import type { Abstand } from "./typen";
import type { Archivvermerk } from "./archivvermerk";

/* ══════════════════════════════════════════════════════════════════
   Der Stand der Auftragsbestätigung aus der Antwort der API (0268).

   Ohne Abhängigkeiten, damit der Test ihn lesen kann. `GET /vorgaenge/:id`
   trägt ihn als `auftragsbestaetigung`, `POST …/auftragsbestaetigung`
   ebenso.
   ══════════════════════════════════════════════════════════════════ */

type Roh = Record<string, unknown>;

const txt = (wert: unknown): string =>
  (wert === null || wert === undefined ? "" : typeof wert === "string" ? wert : String(wert));
const zahl = (wert: unknown): number | null => {
  if (wert === null || wert === undefined || wert === "") return null;
  const n = typeof wert === "number" ? wert : Number(wert);
  return Number.isFinite(n) ? n : null;
};
const liste = (wert: unknown): Roh[] => (Array.isArray(wert) ? (wert as Roh[]) : []);

/**
 * **Der Archivvermerk der Fassung** (P4-06) — aus `archiv`, wie an
 * Protokollen. Eine ältere API kennt nur `archiviert_am`/`gesperrt_bis`; dann
 * daraus, ohne Umgebung.
 */
function vermerkAus(f: Roh): Archivvermerk | undefined {
  /* Dieselbe Lesart wie `archivvermerkAus` (daten/archivvermerk.ts) — hier
     ohne Wertimport, damit der Test diese Datei ohne Bündler lädt. */
  const a = f["archiv"];
  if (a && typeof a === "object") {
    const r = a as Roh;
    const stand = r["stand"];
    const umgebung = r["umgebung"];
    if (stand === "wartet" || stand === "archiviert" || stand === "fehler" || stand === "geloescht") {
      return {
        stand,
        archiviertAm: txt(r["archiviert_am"]) || null,
        gesperrtBis: txt(r["gesperrt_bis"]) || null,
        fassung: zahl(r["fassung"]) ?? 1,
        umgebung: umgebung === "entwicklung" || umgebung === "test" || umgebung === "produktion" ? umgebung : null,
      };
    }
  }
  const am = txt(f["archiviert_am"]);
  if (!am) return undefined;
  return { stand: "archiviert", archiviertAm: am, gesperrtBis: txt(f["gesperrt_bis"]) || null, fassung: 1, umgebung: null };
}

/** Der Stand der AB — fehlt er in der Antwort, gibt es keinen. */
export function abStandAus(roh: unknown): Abstand | null {
  if (!roh || typeof roh !== "object") return null;
  const o = roh as Roh;
  return {
    stamm: txt(o["stamm"]) || null,
    geaendert_seit: txt(o["geaendert_seit"]) || null,
    moeglich: o["moeglich"] === true,
    fassungen: liste(o["fassungen"]).map((f) => {
      const archiv = vermerkAus(f);
      return {
      id: txt(f["id"]),
      nummer: txt(f["nummer"]),
      revision: zahl(f["revision"]) ?? 1,
      am: txt(f["am"]),
      von: txt(f["von"]) || null,
      netto: zahl(f["netto"]),
      brutto: zahl(f["brutto"]),
      archiviert_am: txt(f["archiviert_am"]) || null,
      gesperrt_bis: txt(f["gesperrt_bis"]) || null,
      ...(archiv ? { archiv } : {}),
      versendet: liste(f["versendet"]).map((x) => ({
        am: txt(x["am"]), an: Array.isArray(x["an"]) ? (x["an"] as unknown[]).map(txt) : [],
      })),
      };
    }),
  };
}
